import { ControlContainer, FormGroup } from '@angular/forms';
import { DateAdapter, MatDateFormats, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, ElementRef, ViewChild, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatCalendar } from '@angular/material/datepicker';
import { CustomDateAdapter } from '@shared/adapters/custom-date.adapter';

/** Custom header component for datepicker. */
/* eslint-disable @angular-eslint/component-selector */
@Component({
  selector: 'date-range-picker-header',
  styles: [
    `
      .date-picker-header {
        display: flex;
        align-items: center;
        padding: 0.5em;
        height: 50px;
      }

      .date-picker-header-label {
        flex: 1;
        height: 1em;
        font-weight: 500;
        text-align: center;
      }

      .mat-icon {
        border: 0;
        background-color: #fff;
      }
    `,
  ],
  template: `
    <div class="date-picker-header">
      <button mat-icon-button class="mat-icon" (click)="previousClicked('month')">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chevron-left" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
        </svg>
      </button>
      <span class="date-picker-header-label">{{ periodLabel }}</span>
      <button mat-icon-button class="mat-icon" (click)="nextClicked('month')">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chevron-right" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
        </svg>
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerHeaderComponent<D> implements OnDestroy {
  private _destroyed = new Subject<void>();

  constructor(
    private _calendar: MatCalendar<D>,
    private _dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef
  ) {
    _calendar.stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => cdr.markForCheck());
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  get periodLabel() {
    return this._dateAdapter.format(this._calendar.activeDate, this._dateFormats.display.monthYearA11yLabel).toLocaleUpperCase();
  }

  previousClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month' ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1) : this._dateAdapter.addCalendarYears(this._calendar.activeDate, -1);
  }

  nextClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month' ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1) : this._dateAdapter.addCalendarYears(this._calendar.activeDate, 1);
  }
}

/** Custom Display Selected Date */
export const MY_FORMATS = {
  parse: {
    dateInput: 'dd-MM-yy',
  },
  display: {
    dateInput: 'dd-MM-yy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd-MM-yy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

/** Main Date Range Picker Component */
@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter, deps: [MAT_DATE_LOCALE] },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class DateRangePickerComponent implements OnInit {
  @ViewChild('startDate') startDateRef: ElementRef;
  @ViewChild('endDate') endDateRef: ElementRef;
  @Output() rangeValue = new EventEmitter<FormGroup>();
  @Input() nameStart: string;
  @Input() nameEnd: string;
  @Input() placeholder: string = 'Pilih range tanggal expired';
  @Input() maxDate: Date | null = null;

  constructor(private controlContainer: ControlContainer) {}
  public range: FormGroup;

  get showSeparator(): boolean {
    const start = this.range?.get(this.nameStart)?.value;
    const end = this.range?.get(this.nameEnd)?.value;
    return !!(start || end);
  }
  // range: FormGroup = new FormGroup({
  //   start: new FormControl({value: '', disabled: true}),
  //   end: new FormControl({value: '', disabled: true}),
  // });

  STRING_CONSTANTS = STRING_CONSTANTS;
  dateRangePickerHeader = DateRangePickerHeaderComponent;

  ngOnInit() {
    this.range = <FormGroup>this.controlContainer.control;
  }

  reset() {
    this.range.reset();
  }
}
